import React from 'react';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { cn } from '@rocco/utils/cn';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { FilterConfigModalItemMultiple } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { MultipleValuesFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/multiple-values-filter-state';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';

export const FiltersModalMultipleValues = (
    props: FilterModalElementProps<
        FilterConfigModalItemMultiple,
        MultipleValuesFilterState
    >,
) => {
    const { filterConfigItem, filterState, onFilterStateUpdate, displayMode } =
        props;
    const isMobile = displayMode === DisplayMode.Mobile;
    const {
        searchFilters: { handleToggleMultipleValues },
    } = useFiltersContext();

    const columns = filterConfigItem.columns > 0 ? filterConfigItem.columns : 1;
    const containerStyles = {
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${Math.ceil(filterState.items.length / columns)}, auto)`,
    };

    return (
        // Mobile tightens the container gaps (header-to-first-option pt and
        // inter-option gap) for a denser sheet; the rows themselves keep their
        // >=44px tap target below. Desktop keeps its original spacing.
        <div
            className={cn(
                'grid grid-flow-col-dense',
                isMobile ? 'gap-1 pt-1' : 'gap-2 pt-4',
            )}
            style={containerStyles}
        >
            {filterState.items.map((item) => (
                // A native <label> wrapping the checkbox makes the whole row
                // (not just the label text) an activation target. On mobile
                // this is the primary filter interaction, so the row is grown
                // to a >=44px tap target; desktop keeps its current height.
                <label
                    key={`${filterConfigItem.filterKey}-${item.value}`}
                    htmlFor={`${filterConfigItem.filterKey}-${item.value}`}
                    className={cn(
                        'flex cursor-pointer items-center justify-between',
                        isMobile && 'min-h-11',
                    )}
                >
                    <span className="relative top-[2px] my-1.5 ml-0 text-sm leading-5">
                        {item.label}
                    </span>

                    <RoccoCheckbox
                        id={`${filterConfigItem.filterKey}-${item.value}`}
                        checked={item.checked}
                        onCheckedChange={(checked) => {
                            if (handleToggleMultipleValues) {
                                onFilterStateUpdate(
                                    handleToggleMultipleValues(
                                        filterState,
                                        item,
                                        !!checked,
                                    ),
                                );
                            }
                        }}
                    />
                </label>
            ))}
        </div>
    );
};
